import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { Label } from "@/components/podkit/forms/Label";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteAccount, type PlainAccount } from "@/queries/account-queries";
import { formatError } from "@/utils/errors";
import { useCallback, useId, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const DeleteAccountModal: FC<{ account: PlainAccount; open: boolean; onClose: () => void }> = ({
    account,
    open,
    onClose,
}) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const confirmationID = useId();
    const [confirmationValue, setConfirmationValue] = useState<string>("");
    const deleteAccount = useDeleteAccount();

    const onOpenChange = (open: boolean) => {
        if (!open) {
            deleteAccount.reset();
            onClose();
        }
    };

    const confirmed = useMemo(() => confirmationValue === account.name, [confirmationValue, account.name]);

    const handleDeleteAccount = useCallback(async () => {
        try {
            await deleteAccount.mutateAsync({ accountId: account.id });
            navigate("/account-deleted");
        } catch (error) {
            toast({ title: "Failed to delete account", description: formatError(error) });
        }
        return;
    }, [account.id, deleteAccount, navigate, toast]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-xl"
                data-track-location={TrackLocations.DeleteAccountModal}
                data-testid="delete-account-modal"
            >
                <DialogHeader>
                    <DialogTitle>Delete account</DialogTitle>
                    <DialogDescription />
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <ErrorMessage error={deleteAccount.error} data-testid="account-delete-failure-message" />
                    <div className="text-base text-content-primary">
                        Are you sure you want to permanently delete your account{" "}
                        <span className="font-bold">{account.name}</span>?
                    </div>
                    <div className="text-base text-content-primary">
                        Deleting your Ona account is permanent and irreversible. This action will affect your entire
                        account across all organizations and:
                    </div>

                    <ol className="list-outside list-decimal pl-6 text-base text-content-primary">
                        <li className="pl-1">
                            Remove your access to all <b>projects and environments</b> across all organizations.
                        </li>
                        <li className="pl-1">
                            Remove your account from <b>every organization</b> you are a member of.
                        </li>
                        <li className="pl-1">
                            Revoke access to any <b>runners</b> (we recommend deleting them beforehand).
                        </li>
                        <li className="pl-1">
                            Remove all <b>authentication tokens, access keys, and personal preferences</b>.
                        </li>
                    </ol>

                    <Label htmlFor={confirmationID}>
                        Type <span className="font-bold text-content-primary">{account.name}</span> to confirm
                    </Label>
                    <Input
                        id={confirmationID}
                        type="text"
                        name="confirmation_value"
                        placeholder="Type to confirm"
                        value={confirmationValue}
                        onChange={(e) => setConfirmationValue(e.target.value)}
                    />
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={deleteAccount.isPending}
                            onClick={onClose}
                            data-tracking-id="cancel-delete-account-delete-account-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        autoFocus={true}
                        disabled={!confirmed}
                        loading={deleteAccount.isPending}
                        onClick={handleDeleteAccount}
                        data-testid="delete-account-confirm-btn"
                        data-tracking-id="confirm-delete-account-delete-account-modal"
                    >
                        Yes, Delete Account
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
